import React from 'react';
import { Check, Circle, LoaderCircle, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, message, Progress, Spin } from 'antd';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { ExcellExportResponse } from '@/store/salles/GetExcel/useExcelExport';
import handleDownloadFile from '@/utils/downloadFile';
import dayjs from '@/utils/dayjs';
import { useFakeProgress } from '../progress';
import * as S from '../styled';
import { useRelativeTime } from '../hooks/useRelativeTime';

const getLabel = (status: string) => {
    switch (status) {
        case 'completed':
            return 'Pronto para baixar';
        case 'processing':
            return 'Aguardando...';
        case 'error':
            return 'Erro na exportação';
        default:
            return 'Processando...';
    }
};

type ContractItemProps = {
    item: ExcellExportResponse;
    labelItems?: string;
    shouldAnimate?: boolean;
};

export function ContractItem({
    item,
    labelItems,
    shouldAnimate,
}: ContractItemProps) {
    const progress = useFakeProgress(item.status === 'completed', item.id);

    const handleDownload = async (url: string, filename: string) => {
        try {
            await handleDownloadFile(url, filename);
        } catch (error) {
            message.error(
                'Falha ao baixar o arquivo, tente novamente ou fale com o nosso suporte.',
            );
        }
    };

    const timeLabel = useRelativeTime(item.created_at);

    return (
        <motion.div
            animate={{ opacity: 1, height: 'auto' }}
            initial={shouldAnimate ? { opacity: 0, height: 0 } : false}
            transition={{ duration: 0.3 }}
            exit={shouldAnimate ? { opacity: 0, height: 0 } : undefined}
            style={{ overflow: 'hidden' }}
        >
            <S.ContractsItemDownloadContainer>
                <S.ContractsDownloadContainer>
                    <S.ContractRadiusProgress status={item.status}>
                        {item.status === 'completed' && <Check size={14} />}
                        {(item.status === 'processing' ||
                            item.status === 'pending') && (
                            <LoaderCircle size={14} />
                        )}
                        {item.status === 'error' && <X size={14} />}
                    </S.ContractRadiusProgress>
                    <S.ContractsProgressLabels>
                        <S.ContractsProgressTitle>
                            {labelItems ?? item.type}_
                            {dayjs(item.created_at).format('DD-MM-YYYY')}.xlsx
                        </S.ContractsProgressTitle>
                        <S.ContractsInfoRequest>
                            <S.ContractsRequestItem>
                                <Circle
                                    color="currentColor"
                                    size={11}
                                    fill="currentColor"
                                />
                                Solicitado {timeLabel}
                            </S.ContractsRequestItem>
                        </S.ContractsInfoRequest>
                        {item.status === 'processing' && (
                            <Progress
                                strokeColor={{
                                    '0%': '#FAAD14',
                                    '100%': '#FFD666',
                                }}
                                percent={progress}
                                size="small"
                                status="active"
                                showInfo={false}
                            />
                        )}
                    </S.ContractsProgressLabels>
                </S.ContractsDownloadContainer>
                <S.ContractsProgressActions>
                    <S.Badge status={item.status}>
                        {getLabel(item.status)}
                    </S.Badge>
                    {item.status === 'pending' ||
                        (item.status === 'processing' && (
                            <>
                                <Spin
                                    indicator={
                                        <LoadingOutlined
                                            spin
                                            style={{
                                                color: '#FAAD14',
                                                strokeWidth: '30px',
                                            }}
                                        />
                                    }
                                    size="small"
                                />
                                <S.ProgressText>Processando</S.ProgressText>
                            </>
                        ))}
                    {item.status === 'completed' && (
                        <Button
                            type="primary"
                            onClick={() =>
                                handleDownload(
                                    item.uploadedFile?.url,
                                    item.uploadedFile?.name,
                                )
                            }
                            icon={<DownloadOutlined />}
                        >
                            Baixar
                        </Button>
                    )}
                </S.ContractsProgressActions>
            </S.ContractsItemDownloadContainer>
        </motion.div>
    );
}
